/**
 * CeoCrossBrainPage — Phase 36.7b.
 *
 * Cross-brain narrative connecting events across all 5 OMEGA brains.
 * Endpoint: GET /api/v1/internal/ceo/cross-brain-narrative
 * Poll: 1 h (LLM-backed; C-10 confidence badges enforced).
 *
 * C-10 UncertaintyLive: confidence badges are color-coded on every
 * predictive claim — BrainConnection, Anomaly, and Trend items.
 */

import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

export type Confidence = 'low' | 'medium' | 'high';
export type TrendDirection = 'up' | 'down' | 'stable';

export interface BrainConnection {
  connection_id: string;
  source_brain: string;
  target_brain: string;
  description: string;
  evidence: string[];
  confidence: Confidence;
}

export interface Anomaly {
  anomaly_id: string;
  brain: string;
  description: string;
  metric_name: string;
  observed_value: number;
  severity: number;
}

export interface Trend {
  trend_id: string;
  brain: string;
  description: string;
  direction: TrendDirection;
  confidence: Confidence;
}

export interface CrossBrainNarrative {
  narrative_id: string;
  generated_at: number;
  headline: string;
  connections: BrainConnection[];
  anomalies: Anomaly[];
  trends: Trend[];
  q5_verified: boolean;
  cost_usd: number;
}

// ── Helpers (exported for tests) ─────────────────────────────────────────────

export function confidenceColor(c: string): string {
  switch (c) {
    case 'high':   return '#34d399';
    case 'medium': return '#fbbf24';
    default:       return '#f87171';
  }
}

export function confidenceLabel(c: string): string {
  switch (c) {
    case 'high':   return 'HIGH';
    case 'medium': return 'MED';
    default:       return 'LOW';
  }
}

export function trendDirectionLabel(d: string): string {
  switch (d) {
    case 'up':   return '↑';
    case 'down': return '↓';
    default:     return '→';
  }
}

export function trendDirectionColor(d: string): string {
  switch (d) {
    case 'up':   return '#34d399';
    case 'down': return '#f87171';
    default:     return '#7dd3fc';
  }
}

export function severityColor(s: number): string {
  if (s <= 2) return '#fbbf24';
  if (s === 3) return '#fb923c';
  return '#f87171';
}

export function fmtNarrativeAt(secs: number): string {
  return new Date(secs * 1000).toLocaleString('he-IL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function CeoCrossBrainPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['ceo', 'cross-brain-narrative'],
    queryFn: () => apiFetch<CrossBrainNarrative>('/internal/ceo/cross-brain-narrative'),
    refetchInterval: 3_600_000,
    retry: false,
  });

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',  to: '/omega' },
        { label: 'CEO GI', to: '/omega/ceo' },
        { label: 'Cross-Brain' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', direction: 'ltr' }}>
          Narrative Cross-Brain
        </h1>
        {data && (
          <span style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)' }}>
            {fmtNarrativeAt(data.generated_at)}
          </span>
        )}
        {data?.q5_verified && (
          <span style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 3, fontSize: '7.5pt', padding: '1px 6px', color: '#34d399', letterSpacing: '0.06em' }}>
            Q-5 ✓
          </span>
        )}
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Synthesizing narrative…</div>}
      {isError && (
        <div style={{ color: '#f87171', fontSize: '9pt', direction: 'ltr' }}>
          Error: {(error as Error).message}
        </div>
      )}

      {data && (
        <>
          {/* Headline */}
          <section style={{ marginBottom: 24 }}>
            <div style={{
              background: 'rgba(125,211,252,0.06)',
              border: '1px solid rgba(125,211,252,0.2)',
              borderRadius: 6, padding: '14px 18px', maxWidth: 720,
              fontSize: '10.5pt', fontWeight: 600, lineHeight: 1.5, direction: 'ltr',
            }}>
              {data.headline}
            </div>
          </section>

          {/* Brain connections */}
          {data.connections.length > 0 && (
            <section style={{ marginBottom: 24 }}>
              <SectionLabel label={`Cross-brain connections (${data.connections.length})`} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 720 }}>
                {data.connections.map(conn => (
                  <div key={conn.connection_id} style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '12px 16px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '8pt', fontWeight: 700, color: '#7dd3fc', fontFamily: 'var(--omega-mono)' }}>
                        {conn.source_brain}
                      </span>
                      <span style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>→</span>
                      <span style={{ fontSize: '8pt', fontWeight: 700, color: '#a78bfa', fontFamily: 'var(--omega-mono)' }}>
                        {conn.target_brain}
                      </span>
                      {/* C-10: confidence badge on predictive connection */}
                      <ConfidenceBadge c={conn.confidence} />
                    </div>
                    <div style={{ fontSize: '9pt', direction: 'ltr', marginBottom: 6 }}>{conn.description}</div>
                    {conn.evidence.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {conn.evidence.map((e, i) => (
                          <div key={i} style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', direction: 'ltr', paddingLeft: 10, borderLeft: '2px solid rgba(255,255,255,0.1)' }}>
                            {e}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Anomalies */}
          {data.anomalies.length > 0 && (
            <section style={{ marginBottom: 24 }}>
              <SectionLabel label={`Anomalies (${data.anomalies.length})`} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 720 }}>
                {data.anomalies.map(anomaly => (
                  <div key={anomaly.anomaly_id} style={{
                    display: 'grid', gridTemplateColumns: '46px 90px 1fr 80px',
                    gap: 10, alignItems: 'center', padding: '10px 14px',
                    borderLeft: `3px solid ${severityColor(anomaly.severity)}`,
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4,
                  }}>
                    <span style={{
                      fontSize: '7.5pt', fontWeight: 700, padding: '1px 5px',
                      border: `1px solid ${severityColor(anomaly.severity)}`,
                      color: severityColor(anomaly.severity), borderRadius: 3, textAlign: 'center',
                    }}>
                      {anomaly.severity}/5
                    </span>
                    <span style={{ fontSize: '8pt', color: '#7dd3fc', fontFamily: 'var(--omega-mono)' }}>
                      {anomaly.brain}
                    </span>
                    <span style={{ fontSize: '8.5pt', direction: 'ltr' }}>{anomaly.description}</span>
                    <span style={{ fontSize: '8pt', fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink-subtle)', textAlign: 'right' }}>
                      {anomaly.observed_value.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Trends */}
          {data.trends.length > 0 && (
            <section style={{ marginBottom: 24 }}>
              <SectionLabel label={`Trends (${data.trends.length})`} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 720 }}>
                {data.trends.map(trend => (
                  <div key={trend.trend_id} style={{
                    display: 'grid', gridTemplateColumns: '30px 90px 1fr 70px',
                    gap: 10, alignItems: 'center', padding: '10px 14px',
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4,
                  }}>
                    <span style={{
                      fontSize: '14pt', fontWeight: 700,
                      color: trendDirectionColor(trend.direction),
                      textAlign: 'center',
                    }}>
                      {trendDirectionLabel(trend.direction)}
                    </span>
                    <span style={{ fontSize: '8pt', color: '#7dd3fc', fontFamily: 'var(--omega-mono)' }}>
                      {trend.brain}
                    </span>
                    <span style={{ fontSize: '8.5pt', direction: 'ltr' }}>{trend.description}</span>
                    {/* C-10: confidence badge on predictive trend */}
                    <ConfidenceBadge c={trend.confidence} />
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionLabel({ label }: { label: string }) {
  return (
    <div style={{
      fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10,
      fontFamily: 'var(--omega-mono)', direction: 'ltr',
    }}>
      {label}
    </div>
  );
}

function ConfidenceBadge({ c }: { c: string }) {
  return (
    <span style={{
      fontSize: '7pt', fontWeight: 700, padding: '1px 6px',
      border: `1px solid ${confidenceColor(c)}`,
      color: confidenceColor(c), borderRadius: 3,
      letterSpacing: '0.08em',
    }}>
      {confidenceLabel(c)}
    </span>
  );
}
